import { AuthContext, useBodyBottomPadding } from '@repo-level/core-front';
import useEventListener from '@repo-level/core-front/hooks/eventListener';
import { AnalyticsEventName } from 'api/analytics';
import { AuthButtons } from 'components/auth/AuthButtons';
import { ModalsContext, ModalType } from 'contexts/modal';
import { RealTimeServiceContext } from 'contexts/realTimeService';
import { TabBarContext } from 'contexts/tabBarSize';
import { useAnalytics } from 'helpers/analytics';
import { useLuckyWheel } from 'hooks/luckyWheel';
import { useTheme } from 'hooks/theme';
import { useAuroraCrystalAvailable } from 'modules/crystal/hooks';
import { sendSearchOverallAppearanceEvent } from 'modules/googleAnalytics/events/searchGamesEvents';
import { getSpinsLeftAmount } from 'modules/wheel/components/helpers';
import { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigation } from 'react-navi';
import { ScTabBar } from './styled';

export const TabBar = () => {
  const {
    i18n: { language },
  } = useTranslation('glossary');
  const { navigate } = useNavigation();

  const ref = useRef<HTMLDivElement>(null);

  const { isAuthenticated, user } = useContext(AuthContext);
  const { handleModal } = useContext(ModalsContext);
  const { lootboxes } = useContext(RealTimeServiceContext);
  const { handleTabBarHeight } = useContext(TabBarContext);

  const { setBodyBottomPadding } = useBodyBottomPadding();
  const { sendEventToAnalytics } = useAnalytics();
  const { menu, hamburgerIcon: HamburgerIcon } = useTheme();
  const { isExtraLootbox } = useLuckyWheel();
  const { isDateValid, isUserValid } = useAuroraCrystalAvailable();

  useEventListener('resize', () => handleTabBarHeight(ref), undefined, true);

  useEffect(() => {
    const handleScroll = () => {
      if (ref == null || ref.current == null) return;
      setBodyBottomPadding(ref.current.offsetHeight);
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [setBodyBottomPadding]);

  const {
    sendPromoButtonAnalytics,
    sendWheelButtonAnalytics,
    sendMenuButtonAnalytics,
    sendLoginButtonAnalytics,
    sendRegisterButtonAnalytics,
    sendHomeButtonAnalytics,
    sendSearchButtonAnalytics,
  } = useMemo(
    () => ({
      sendWheelButtonAnalytics: () =>
        sendEventToAnalytics({
          name: AnalyticsEventName.TABBAR__WHEEL_BUTTON__CLICK,
        }),
      sendPromoButtonAnalytics: () =>
        sendEventToAnalytics({
          name: AnalyticsEventName.TABBAR__PROMO_BUTTON__CLICK,
        }),
      sendMenuButtonAnalytics: () =>
        sendEventToAnalytics({
          name: AnalyticsEventName.TABBAR__MENU_BUTTON__CLICK,
        }),
      sendLoginButtonAnalytics: () =>
        sendEventToAnalytics({
          name: AnalyticsEventName.TABBAR__LOGIN_BUTTON__CLICK,
        }),
      sendRegisterButtonAnalytics: () =>
        sendEventToAnalytics({
          name: AnalyticsEventName.TABBAR__REGISTER_BUTTON__CLICK,
        }),
      sendHomeButtonAnalytics: () =>
        sendEventToAnalytics({
          name: AnalyticsEventName.TABBAR__HOME_BUTTON__CLICK,
        }),
      sendSearchButtonAnalytics: () =>
        sendEventToAnalytics({
          name: AnalyticsEventName.TABBAR__SEARCH_BUTTON__CLICK,
        }),
      sendTournamentsButtonAnalytics: () =>
        sendEventToAnalytics({
          name: AnalyticsEventName.TABBAR__TOURNAMENTS_BUTTON__CLICK,
        }),
    }),
    [sendEventToAnalytics]
  );

  const BonusesButton = useCallback(
    () => (
      <li>
        <button
          onClick={() => {
            sendPromoButtonAnalytics();
            void navigate('/bonuses');
          }}
          type="button"
        >
          <menu.fortuneIconL.component />
        </button>
      </li>
    ),
    [menu, navigate, sendPromoButtonAnalytics]
  );

  const BurgerMenuButton = useCallback(
    () => (
      <li>
        <button
          onClick={() => {
            sendMenuButtonAnalytics();
            handleModal(ModalType.TabbarMenu)();
          }}
          type="button"
        >
          <HamburgerIcon />
        </button>
      </li>
    ),
    [HamburgerIcon, handleModal, sendMenuButtonAnalytics]
  );

  const AuthButtonsComp = () => (
    <AuthButtons
      horizontal={true}
      onJoinNowClick={sendRegisterButtonAnalytics}
      onLoginClick={sendLoginButtonAnalytics}
    />
  );

  const handleSearchClick = useCallback(() => {
    sendSearchOverallAppearanceEvent('tabbar');
    sendSearchButtonAnalytics();
    void navigate('/search-games');
  }, [navigate, sendSearchButtonAnalytics]);

  const isWheelVisible = isDateValid ? isUserValid : true;
  return (
    <ScTabBar
      className={language}
      isAuthenticated={isAuthenticated}
      isWheelVisible={isWheelVisible}
      ref={ref}
    >
      <ul className="menu-list">
        {!isAuthenticated && (
          <li>
            <AuthButtonsComp />
          </li>
        )}

        <li>
          <button
            onClick={() => {
              sendHomeButtonAnalytics();
              void navigate('/');
              setTimeout(() => window.scrollTo(0, 0), 0);
            }}
            type="button"
          >
            <menu.homeIconLarge.component />
          </button>
        </li>

        {isAuthenticated && (
          <>
            <BonusesButton />

            {isWheelVisible && (
              <li>
                <button
                  onClick={() => {
                    sendWheelButtonAnalytics();
                    void navigate('/lucky-wheel');
                  }}
                  type="button"
                >
                  <menu.wheelIconL.component />
                  <div className="wheel-spins">
                    {isExtraLootbox ? (
                      <menu.star.component />
                    ) : (
                      getSpinsLeftAmount(lootboxes || [], user)
                    )}
                  </div>
                </button>
              </li>
            )}

            <li>
              <button onClick={handleSearchClick} type="button">
                <menu.searchIconLarge.component />
              </button>
            </li>
          </>
        )}

        <BurgerMenuButton />
      </ul>
    </ScTabBar>
  );
};
